import { useEffect, useMemo, useRef, useState } from "react";
import Image from "next/image";
import { MdOutlineShare, MdHeadsetMic, MdOutlineMap } from "react-icons/md";
import { IoMdShareAlt } from "react-icons/io";
import { FaStar, FaWhatsapp } from "react-icons/fa";
import { FaLocationDot } from "react-icons/fa6";
import MainLayout from "@/Component/MainLayout";
import Breadcrumbs from "@/Component/Common/Breadcrumbs";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { fetchActivityDetailsBySlug } from "@/store/slices/activitySlice";
import { getCurrencySymbol } from "@/utils/getCurrencySymbol";
import { formatIndianPrice } from "@/utils/formatIndianPrice";
import { isIndianUser } from "@/utils/getCountryCode";
import ActivitySummary from "./ActivitySummary";
import ActivityGallery from "./ActivityGallery";
import EnquiryModal, { ENQUIRY_MODULE } from "@/Component/Common/EnquiryModal";
import HolidayPackageSection from "@/Component/Common/HolidayPackageSection";
import NotFound from "@/Component/Module/NotFound";
import { isInvalidDetailSlug, isMissingActivityDetail } from "@/utils/detailSlug";
import { fetchSimilarActivities } from "@/services/api/activities";
import { scrollToSectionElement } from "@/utils/scrollToSection";
import useHideBarWhenSectionVisible from "@/hooks/useHideBarWhenSectionVisible";
import styles from "./ActivityDetail.module.css";
import { FaMap } from "react-icons/fa";
import { IoLogoWhatsapp } from "react-icons/io";
import { getCallUrl, getWhatsAppUrl, PHONE_NUMBERS } from "@/utils/contactNumbers";

const DEFAULT_IMAGE =
  "https://blueberrydatastorage.blob.core.windows.net/websitesdata/NH/noimage.jpg";

function PriceLabel({ amount, isSpecial, variant = "main", size = 14 }) {
  const value = Number(amount) || 0;
  if (isSpecial) {
    return <>USD {formatIndianPrice(value)}</>;
  }
  if (isIndianUser()) {
    return <>₹ {formatIndianPrice(value)}</>;
  }
  return (
    <>
      {getCurrencySymbol(variant, size)} {formatIndianPrice(value)}
    </>
  );
}

function MobileIncludes({ items }) {
  if (!items.length) return null;

  return (
    <div className={`${styles.includesBox} ${styles.mobileIncludes}`}>
      <h2 className={styles.includesTitle}>Included in This Activity Package</h2>
      <div className={styles.includesGrid}>
        {items.map((item, index) => (
          <div key={`${item.label}-${index}`} className={styles.includeItem}>
            <Image
              src={item.icon}
              alt={item.label}
              width={20}
              height={20}
              className={styles.includeIcon}
              unoptimized
              quality={70}
              loading="lazy"
            />
            <span>{item.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ContactCards({ activityName, className = "", showAgent = true }) {
  const phone = PHONE_NUMBERS.uae;

  return (
    <div className={`${styles.contactRow} ${className}`}>
      <button
        className={styles.contactCard}
        onClick={() => window.open(getWhatsAppUrl(phone.whatsapp, `Hi, I am interested in ${activityName}`), "_blank", "noopener,noreferrer")}
      >
        <span className={`${styles.contactIcon} ${styles.whatsapp}`}>
          <IoLogoWhatsapp size={22} />
        </span>
        <span>Enquire via WhatsApp</span>
      </button>
      {showAgent ? (
        <button
          type="button"
          className={styles.contactCard}
          onClick={() => window.location.href = getCallUrl(phone.call)}
        >
          <span className={`${styles.contactIcon} ${styles.agent}`}>
            <MdHeadsetMic size={22} />
          </span>
          <span>Talk to our Agent</span>
        </button>
      ) : null}
    </div >
  );
}

export default function ActivityDetail({ activitySlug, initialDetail = null }) {
  const dispatch = useAppDispatch();
  const { detail: storeDetail, detailLoading, detailError } = useAppSelector(
    (s) => s.activity
  );
  const detail = storeDetail || initialDetail?.data || initialDetail || null;
  const selectPackageRef = useRef(null);
  const sidebarRef = useRef(null);
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const [similarSectionData, setSimilarSectionData] = useState(null);
  const [showStickyTitle, setShowStickyTitle] = useState(false);

  useEffect(() => {
    if (initialDetail || !activitySlug) return;
    dispatch(fetchActivityDetailsBySlug(activitySlug));
  }, [dispatch, activitySlug, initialDetail]);

  const activityId = detail?.activity?.activityId || detail?.activities?.activityId;
  const hideMobilePriceBar = useHideBarWhenSectionVisible("select-package", activityId);

  useEffect(() => {
    if (!activityId) {
      setSimilarSectionData(null);
      return undefined;
    }

    let cancelled = false;

    fetchSimilarActivities(activityId)
      .then((response) => {
        if (!cancelled) setSimilarSectionData(response?.data);
      })
      .catch(() => {
        if (!cancelled) setSimilarSectionData(null);
      });

    return () => {
      cancelled = true;
    };
  }, [activityId]);

  const data = detail || {};
  const activity = data.activity || data.activities || {};
  const locationInfo = data.location || {};
  const boxInSideBar = data.boxInSideBar || {};
  const reviews = data.reviews || {};
  const activityName = activity.activityName || activity.name || "Activity";

  useEffect(() => {
    const sidebar = sidebarRef.current;
    if (!sidebar) return undefined;

    const STICKY_TOP = 90;
    const desktopQuery = window.matchMedia("(min-width: 1101px)");

    const update = () => {
      if (!desktopQuery.matches) {
        setShowStickyTitle(false);
        return;
      }
      setShowStickyTitle(sidebar.getBoundingClientRect().top <= STICKY_TOP + 1);
    };

    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    desktopQuery.addEventListener("change", update);

    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
      desktopQuery.removeEventListener("change", update);
    };
  }, [activityName]);
  const city = locationInfo.cityName || activity.cityName || "City";
  const country = locationInfo.countryName || activity.countryName || "Country";

  const location = [city, country].filter(Boolean).join(", ");
  const durationLabel = String(activity.duration || "").trim();
  const isSpecial = Boolean(activity.isSpecialActivity);

  const galleryImages = useMemo(() => {
    if (!data?.images?.length) return [];

    const sortedImages = [...data.images].sort((a, b) => {
      if (a.isFrontImage === 1) return -1;
      if (b.isFrontImage === 1) return 1;
      if (a.isFrontImage === 2) return -1;
      if (b.isFrontImage === 2) return 1;
      return 0;
    });

    return sortedImages.map((img, index) => ({
      imagePath: img.path || DEFAULT_IMAGE,
      alternatetext: img.alternateText || "Activity image",
      isFrontImage: img.isFrontImage,
      originalPath: img.path,
      index,
    }));
  }, [data.images]);

  const includeItems = useMemo(() => {
    const includesData = data.includes || [];
    return includesData
      .filter((item) => item.includesTypeName && item.includesImagePath)
      .map((item) => ({
        label: item.includesTypeName,
        icon: item.includesImagePath,
      }));
  }, [data.includes]);

  const cutPrice = boxInSideBar.displayCutPrice;
  const price = boxInSideBar.price;
  const saveAmount = cutPrice - price;
  const promoTag = boxInSideBar.tag2 || "";
  const rating = Number(reviews.averageRating ?? activity.rating ?? 0) || 0;
  const reviewCount =
    Number(reviews.totalReviews ?? activity.noOfReview ?? activity.noOfReviews) ||
    0;

  const handleShare = () => {
    if (typeof navigator !== "undefined" && navigator.share) {
      navigator
        .share({ title: activityName, url: window.location.href })
        .catch(() => { });
      return;
    }
    alert("Share coming soon");
  };

  const handleOpenMap = () => {
    if (!location || typeof window === "undefined") return;
    const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const scrollToSelectPackage = () => {
    const el = selectPackageRef.current || document.getElementById("select-package");
    if (!el) return;
    scrollToSectionElement(el);
  };

  if (
    isInvalidDetailSlug(detail) ||
    isInvalidDetailSlug(initialDetail) ||
    (detail && isMissingActivityDetail(detail)) ||
    (initialDetail && isMissingActivityDetail(initialDetail))
  ) {
    return <NotFound />;
  }

  if (detailLoading && !detail) {
    return (
      <MainLayout hideHeader>
        <div className={`standard-padding ${styles.page}`}>
          <p className={styles.stateMsg}>Loading activity details...</p>
        </div>
      </MainLayout>
    );
  }

  if (detailError || !detail || !activity?.activityId) {
    return <NotFound />;
  }

  return (
    <MainLayout hideHeader>
      <Breadcrumbs />
      <div className={`standard-padding ${styles.page}`}>
        <div className={styles.titleBlock}>
          <div className={styles.heroDesktop}>
            <div className={styles.titleLine}>
              <h1 className={styles.title}>{activityName}</h1>
              {durationLabel ? (
                <span className={styles.durationBadge}>{durationLabel}</span>
              ) : null}
            </div>

            <div className={styles.headerFooter}>
              <p className={styles.location}>
                <FaLocationDot size={14} aria-hidden="true" />
                <span>{location}</span>
              </p>
              <div className={styles.actions}>
                {/* <button
                  type="button"
                  className={styles.iconBtn}
                  aria-label="Share"
                >
                  <FaMap size={22} />
                </button> */}
                <button
                  type="button"
                  className={styles.iconBtn}
                  aria-label="Share"
                  onClick={handleShare}
                >
                  <IoMdShareAlt size={22} />
                </button>
              </div>
            </div>
          </div>

          <div className={styles.heroMobile}>
            <div className={styles.heroTitleRow}>
              <h1 className={styles.title}>{activityName}</h1>
              <div className={styles.heroActions}>
                {/* {location ? (
                  <button
                    type="button"
                    className={styles.iconBtn}
                    aria-label="View on map"
                    onClick={handleOpenMap}
                  >
                    <MdOutlineMap size={22} />
                  </button>
                ) : null} */}
                <button
                  type="button"
                  className={styles.iconBtn}
                  aria-label="Share"
                  onClick={handleShare}
                >
                  <IoMdShareAlt size={22} />
                </button>
              </div>
            </div>
            {location ? (
              <p className={styles.location}>
                <FaLocationDot size={13} aria-hidden="true" />
                <span>{location}</span>
              </p>
            ) : null}
          </div>
        </div>

        <ActivityGallery title={activityName} images={galleryImages} />

        <ContactCards activityName={activityName} className={styles.mobileContactCards} />

        <MobileIncludes items={includeItems} />

        <div className={styles.bodyLayout}>
          <div className={styles.mainCol}>
            <ActivitySummary
              data={data}
              selectPackageRef={selectPackageRef}
              hideIncludesOnMobile
            />
          </div>

          <aside ref={sidebarRef} className={styles.sidebar}>
            <div className={`${styles.priceCard} ${styles.desktopSidebarPrice}`}>
              {showStickyTitle && activityName ? (
                <p className={styles.stickyPriceTitle}>{activityName}</p>
              ) : null}
              <div className={styles.priceTag}>
                {promoTag ? (
                  <span className={styles.offerTag}>{promoTag}</span>
                ) : (
                  <span />
                )}
                <div className={styles.ratingCol}>
                  {rating > 0 ? (
                    <div className={styles.ratingBadge}>
                      {rating.toFixed(1)} <FaStar size={11} />
                    </div>
                  ) : null}
                  {reviewCount ? (
                    <span className={styles.reviewLink}>{reviewCount} Reviews</span>
                  ) : null}
                </div>
              </div>
              <div className={styles.priceRow}>
                <div className={styles.pricing}>
                  {cutPrice ? (
                    <p className={styles.priceOld}>
                      Starts From{" "}
                      <span style={{ textDecoration: "line-through" }}>
                        <PriceLabel
                          amount={cutPrice}
                          isSpecial={isSpecial}
                          variant="sub"
                          size={10}
                        />
                      </span>
                    </p>
                  ) : null}

                  <p className={styles.priceNow}>
                    <PriceLabel
                      amount={price}
                      isSpecial={isSpecial}
                      variant="main"
                      size={20}
                    />
                  </p>
                  <p className={styles.priceNote}>Per Person</p>
                </div>
                {saveAmount > 0 ? (
                  <span className={styles.saveTag}>
                    Save{" "}&nbsp;
                    <PriceLabel
                      amount={saveAmount}
                      isSpecial={isSpecial}
                      variant="green"
                      size={10}
                    />
                  </span>
                ) : null}
              </div>
              <button
                type="button"
                className={styles.proceedBtn}
                onClick={scrollToSelectPackage}
              >
                Proceed
              </button>
              <button
                type="button"
                className={styles.enquiryBtn}
                onClick={() => setEnquiryOpen(true)}
              >
                Send Enquiry
              </button>
            </div>

            <ContactCards
              activityName={activityName}
              showAgent={false}
              className={styles.desktopContactCards}
            />
          </aside>
        </div>
      </div>

      {similarSectionData ? (
        <HolidayPackageSection
          title="Similar Activities"
          subtitle="Explore the experiences you dream of"
          moduleType="activity"
          data={similarSectionData}
          hideWhenEmpty
        />
      ) : null}

      <div
        className={`${styles.mobilePriceBar} ${
          hideMobilePriceBar ? styles.mobilePriceBarHidden : ""
        }`}
      >
        <div className={styles.mobilePriceInfo}>
          {cutPrice > price ? (
            <p className={styles.mobilePriceOld}>
              Starts From&nbsp;
              <div style={{ textDecoration: "line-through", display: "flex", flexWrap: "nowrap" }}>
                <PriceLabel amount={cutPrice} isSpecial={isSpecial} variant="white" size={9} />
              </div>
            </p>
          ) : null}
          <p className={styles.mobilePriceNow}>
            <PriceLabel amount={price} isSpecial={isSpecial} variant="white" size={16} />
          </p>
          <p className={styles.mobilePriceNote}>Per Person</p>
        </div>
        <div className={styles.mobilePriceActions}>
          <button
            type="button"
            className={styles.mobileEnquiryBtn}
            onClick={() => setEnquiryOpen(true)}
          >
            Send Enquiry
          </button>
          <button
            type="button"
            className={styles.mobileProceedBtn}
            onClick={scrollToSelectPackage}
          >
            Proceed
          </button>
        </div>
      </div>

      <EnquiryModal
        open={enquiryOpen}
        onClose={() => setEnquiryOpen(false)}
        moduleType={ENQUIRY_MODULE.ACTIVITY}
        title={activityName}
        subtitle={location}
        image={galleryImages?.[0]?.imagePath || DEFAULT_IMAGE}
        price={price}
        cutPrice={cutPrice}
        discountTag={promoTag}
        pageUrlName={activityName}
      />
    </MainLayout>
  );
}
